import Schema from '../Schema';

import ShoppingDiscounts from './ShoppingDiscounts';
import PremiumPlans from './PremiumPlans';
import {
  PAYMENT_SYSTEM,
  PAYMENT_SYSTEM_PATTERN
} from '../../../../../../lib/document/schema/payment_system';
import { Card, Payment } from './Payment';

const Orders = new Schema({
  _id: String,

  // オーダー日
  ordered_at: Date,

  state: String,

  // lib/document/schema/payment_system.js
  payment_system: {
    type: String,
    enum: PAYMENT_SYSTEM_PATTERN
  },

  // 決済情報
  payment_id: String,
  payment: {
    customer_id: String,
    card: {
      type: {
        type: String
      },
      name: String,
      last4: String,
      address: {
        name: String,
        line1: String,
        line2: String,
        city: String,
        state: String,
        zip: String,
        country: String
      }
    }
  },

  // 発送
  shipments: [
    {
      type: String,
      ref: 'Shipments'
    }
  ],

  note: String,

  wishlist_user: String,

  premium: {
    // 決済時に適用されたプラン
    plan: {
      type: String,
      ref: PremiumPlans
    },
    // 購入時に TOM Premium に契約した Premium 情報
    transaction: {
      plan: String,
      plan_type: String,
      term: Number,
      recontract_extra_point: Number,
      paypal_payment_id: String
    },
    // Applied point rate at time of creation/purchase (for renewal members)
    point_rate_applied: Number,
    // RenewalしたP会員に特典として付与したポイント
    acquired_point: Number,
    // annual plan 購入した場合 true
    buy_with_annual_plan: Boolean
  },

  // 利用したクーポン
  discount: {
    type: String,
    ref: ShoppingDiscounts
  },

  // クーポンの実際の割引金額
  discount_real_price_with_coupon: Number,

  // 使用したポイント数
  used_point: Number,

  // 割引前の金額 (Product Subtotal)
  product_subtotal: Number,

  // 送料、Import Feesを含まない請求金額
  order_subtotal: Number,

  // 商品割引額
  sku_discount_price_total: Number,

  gift_wrapping_total_charge: Number,

  // キャンセル済みの商品のギフトラッピング料を引いたgift_wrapping_total_charge
  gift_wrapping_total_charge_without_refunded: Number,

  // キャンセル済みの商品の送料を引いたshipping_charge
  shipping_charge_without_refunded: Number,

  // キャンセル済みの商品の割引前の送料を引いたshipping_charge
  actual_shipping_charge_without_refunded: Number,

  // キャンセル済みの商品のImport Feesを引いたimport_fees
  import_fees_without_refunded: Number,

  // キャンセル済みの商品の金額を引いたfixed_grand_total
  fixed_grand_total_without_refunded: Number,

  // キャンセル済みの商品の金額を引いたtotal
  total_without_refunded: Number,

  display_acquired_point: String,

  // 購入時の配送先情報。実際の配送先は shipments に格納
  // Billing address に等しい
  shipping_address: {
    name: String,
    country: String,
    zip: String,
    state: String,
    city: String,
    line1: String,
    line2: String,
    tel: String
  },

  // orders && orders.premium && orders.premium.plan
  has_premium_plan: Boolean
});

Orders.PAYMENT_SYSTEM = PAYMENT_SYSTEM;

// Wish list ギフト購入かどうか
Orders.isWishList = function (order) {
  return !!order.wishlist_user;
};

// クーポンが使用されたかどうか（要populate）
Orders.isUsedCoupon = function (order) {
  return !!(order.discount && order.discount.coupon);
};

// 決済情報別の画像パスを取得
Orders.getPaymentMethodImagePath = function (order) {
  let src = '';
  let alt = order.payment_system;
  if (order.payment_system === Payment.PAYMENT_METHOD.STRIPE) {
    switch (order.payment.card.type) {
      case Card.CARD_BRANDS.VISA:
        src = 'visa.png';
        break;
      case Card.CARD_BRANDS.AMEX:
        src = 'amex.png';
        break;
      case Card.CARD_BRANDS.MASTER:
        src = 'master.png';
        break;
      case Card.CARD_BRANDS.DISCOVER:
        src = 'discover.png';
        break;
      case Card.CARD_BRANDS.JCB:
        src = 'jcb.png';
        break;
      case Card.CARD_BRANDS.DINERS:
        src = 'diners.png';
        break;
    }
    src = `brands/${src}`;
    alt = order.payment.card.type;
  } else if (order.payment_system === Payment.PAYMENT_METHOD.PAYPAL) {
    src = 'card_paypal.png';
  } else if (order.payment_system === Payment.PAYMENT_METHOD.AMAZON) {
    src = 'card_amazon.png';
  }

  return {
    src: `/static/common/icons/${src}`,
    alt
  };
};

// 決済情報別の名称を取得
Orders.getPaymentMethodLabel = function (order) {
  if (order.payment_system === Payment.PAYMENT_METHOD.STRIPE) {
    return `Card Ending in ${order.payment.card.last4}`;
  } else if (order.payment_system === Payment.PAYMENT_METHOD.PAYPAL) {
    return 'PayPal';
  } else if (order.payment_system === Payment.PAYMENT_METHOD.AMAZON) {
    return 'Amazon Pay';
  }
  return '';
};

// 購入時適用された TOM Premium があるかどうか
Orders.hasPremiumPlan = function (order) {
  return !!(order.premium && order.premium.plan);
};

// 購入時適用された TOM Premium を取得
Orders.getPremiumPlan = function (order) {
  if (!Orders.hasPremiumPlan(order)) {
    return undefined;
  }
  return PremiumPlans.findById(order.premium.plan);
};

// 購入時に TOM Premium に契約したかどうか
Orders.hasPremiumTransaction = function (order) {
  return !!(
    order.premium &&
    order.premium.transaction &&
    order.premium.transaction.plan
  );
};

// 購入時に TOM Premium に契約した際のプランタイプ
Orders.getPremiumTransactionPlanType = function (order) {
  if (!(Orders.hasPremiumPlan(order) && Orders.hasPremiumTransaction(order))) {
    return undefined;
  }
  const premiumPlan = Orders.getPremiumPlan(order);
  return premiumPlan.type[order.premium.transaction.plan_type];
};

// 購入時に TOM Premium に契約した際の入会チャージ額
Orders.getPremiumSubscriptionCharge = function (order) {
  if (!(Orders.hasPremiumPlan(order) && Orders.hasPremiumTransaction(order))) {
    return 0;
  }
  return Orders.getPremiumTransactionPlanType(order).price.value;
};

// 購入時に Annual Plan を申し込んだ場合 true
Orders.buyWithAnnualPlan = function (order) {
  return order.premium && order.premium.buy_with_annual_plan;
};

export default Orders;
